<template>
  <v-container fill-height fluid class="justify-center">
    <TermMasterResultDialog ref="resultDialog"></TermMasterResultDialog>
    <ShareDialog ref="shareDialog"></ShareDialog>
    <ErrorDialog ref="errorDialog"></ErrorDialog>

    <v-row>
      <v-col cols="12" sm="4" lg="2" v-if="!parameters.mobile">
        <material-stats-card
          color="blue"
          icon="mdi-access-point-network"
          title="TELETERM"
          :value="statistics.teleterm_rows"
          sub-text="Telecommunications"
          sub-icon="mdi-database"
        />
      </v-col>

      <v-col cols="12" sm="4" lg="2" v-if="!parameters.mobile">
        <material-stats-card
          color="green"
          icon="mdi-desktop-classic"
          title="INFORTERM"
          :value="statistics.inforterm_rows"
          sub-icon="mdi-database"
          sub-text="Information technology"
        />
      </v-col>

      <v-col cols="12" sm="4" lg="2" v-if="!parameters.mobile">
        <material-stats-card
          color="pink"
          icon="mdi-head-outline"
          title="TERMTERM"
          :value="statistics.termterm_rows"
          sub-icon="mdi-database"
          sub-text="Terminology science"
        />
      </v-col>

      <v-col cols="12" sm="4" lg="2" v-if="!parameters.mobile">
        <material-stats-card
          color="orange"
          icon="mdi-ear-hearing"
          title="ACOUTERM"
          :value="statistics.acouterm_rows"
          sub-icon="mdi-database"
          sub-text="Acoustics"
        />
      </v-col>
      <v-col cols="12" sm="4" lg="2" v-if="!parameters.mobile">
        <material-stats-card
          color="red"
          icon="mdi-flash-outline"
          title="ELECTERM"
          :value="statistics.electerm_rows"
          sub-icon="mdi-database"
          sub-text="Electrotechnology"
        />
      </v-col>
      <v-col cols="12" sm="4" lg="2" v-if="!parameters.mobile">
        <material-stats-card
          color="purple"
          icon="mdi-video-input-antenna"
          title="LEXITERM"
          :value="statistics.lexiterm_rows"
          sub-icon="mdi-database"
          sub-text="Telecommunications"
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="8">
        <material-card>
          <template v-slot:header>
            <div class="px-3">
              <div class="title mb-2">TermMaster</div>
              <div v-if="statistics.games > 0" title="Statistics are calculated via cookies so they are browser specific.">
                You have tried to help Nicomachus {{ statistics.games }} times. You succeeded {{ statistics.wins }} times.
              </div>
            </div>
          </template>

          <template v-slot:header_suffix>
            <v-btn :disabled="game.term_uid === 0" icon v-ripple title="send this term to a friend!" @click.prevent="share">
              <v-icon>mdi-share-variant</v-icon>
            </v-btn>
          </template>

          <v-container class="py-0">
            <v-row>
              <v-col lg="9" md="9">
                <v-row>
                  <v-col cols="12" lg="12" md="12">
                    <h2 class="title font-weight-light mb-3">
                      Greek term: <span v-if="game.key['GR']">{{ greek_term_length }} characters</span>
                    </h2>
                  </v-col>
                  <v-col cols="12" lg="12" md="12">
                    <h1>
                      <span class="key_letter" v-for="(symbol, index) in game.key['GR']" v-bind:key="index">{{ symbol }}</span>
                    </h1>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" lg="12" md="12">
                    <h2 class="title font-weight-light mb-3">
                      English term: <span v-if="game.key['EN']">{{ english_term_length }} characters</span>
                    </h2>
                  </v-col>
                  <v-col cols="12" lg="12" md="12">
                    <h1>
                      <span class="key_letter" v-for="(symbol, index) in game.key['EN']" v-bind:key="index">{{ symbol }}</span>
                    </h1>
                  </v-col>
                </v-row>
              </v-col>

              <v-col lg="3" md="3">
                <Hangman :wrongCount="game.failures" v-if="!parameters.mobile"></Hangman>
                <v-row class="justify-end">
                  <v-col class="text-right" lg="12" md="12">
                    <div>
                      {{ prompt }}
                    </div>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-container>
          <template v-slot:actions>
            <v-row>
              <v-col cols="12" lg="6" md="6">
                <Keyboard ref="keyboard_gr" language="GR" @keypress="move" :dense="parameters.mobile" :active="game.playing" />
              </v-col>
              <v-col cols="12" lg="6" md="6">
                <Keyboard ref="keyboard_en" language="EN" @keypress="move" :dense="parameters.mobile" :active="game.playing" />
              </v-col>
            </v-row>
          </template>
        </material-card>
      </v-col>

      <v-col cols="12" md="4">
        <material-card class="v-card-profile">
          <v-avatar slot="offset" class="mx-auto d-block elevation-6" size="130">
            <img src="/img/nicomachus.png" />
          </v-avatar>
          <v-card-text class="text-center">
            <h6 class="overline mb-3">Mathematician / Music Theorist</h6>
            <h4 class="font-weight-light">Nicomachus of Gerasa</h4>
            <p>
              Nicomachus of Gerasa was an important ancient mathematician and music theorist, best known for his works
              <i>Introduction to Arithmetic</i> and <i>Manual of Harmonics in Greek</i>.
            </p>
            <p>
              In his afterlife, after more than 2000 years of research in the ethereal plane, he discovered a termbank with
              {{ total_terms }} terms and he is trying to understand them. Can you help him solve the puzzle?
            </p>
            <p>
              Total investigations: <strong>{{ statistics.total_games }}</strong>
            </p>
            <v-row>
              <v-col cols="12" md="6">
                <v-select :items="databaseOptions" v-model="input.database" item-value="value" item-text="title" label="Termbase"></v-select>
              </v-col>
              <v-col cols="12" md="6">
                <v-checkbox v-model="input.allow_symbols" label="Allow symbols" title="Select if you want terms to include special symbols"></v-checkbox>
              </v-col>
            </v-row>
            <v-btn v-if="!game.playing" color="success" @click.prevent="start" title="Start a new game">
              Begin investigation
            </v-btn>
            <v-btn v-if="game.playing" color="error" @click.prevent="quit">Quit</v-btn>
            <div v-if="development">
              <v-btn v-if="game.playing" color="info" @click.prevent="gameOver(true)">win</v-btn>
              <v-btn v-if="game.playing" color="info" @click.prevent="gameOver(false)">lose</v-btn>
            </div>
            <v-row>
              &nbsp;
            </v-row>

            <v-expansion-panels>
              <v-expansion-panel>
                <v-expansion-panel-header>Advanced options</v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="input.max_len_term_gr"
                        label="Max TERMGR length"
                        type="number"
                        min="0"
                        title="Limit the length of the greek term. 0 is no limit."
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="input.max_len_term_en"
                        label="Max TERMEN length"
                        type="number"
                        min="0"
                        title="Limit the length of the english term. 0 is no limit."
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="game.max_timer"
                        label="Time limit (0 for none)"
                        type="number"
                        min="0"
                        title="Limit the available time for a solution."
                      />
                    </v-col>
                    <v-col cols="12" md="6"></v-col>
                    <v-col cols="12" md="4">
                      <v-btn @click="setDifficulty(0, 0)" color="warning" title="Terms may have any length">Easy</v-btn>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-btn @click="setDifficulty(12, 12)" color="warning" title="The length is limited (close to median)">Medium</v-btn>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-btn @click="setDifficulty(8, 8)" color="warning" title="The length is very limited">Hard</v-btn>
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-card-text>
        </material-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import WordMasterService from "@/app/api/WordMasterService";
import Keyboard from "@/components/keyboard/Keyboard";
import CookieManager from "@/app/helpers/CookieManager";
import TermMasterResultDialog from "@/components/dialog/TermMasterResultDialog";
import ShareDialog from "@/components/dialog/ShareDialog";
import ErrorDialog from "@/components/dialog/ErrorDialog";
import Hangman from "@/components/graphics/Hangman";
import { sanitize, mask, count_symbols } from "@/app/helpers/StringHelpers";
import { Resizable } from "@/components/mixins/Resizable";
import { DevelopmentModeDetector } from "@/components/mixins/DevelopmentModeDetector";

const wordMasterService = new WordMasterService();
const cookieService = new CookieManager();

String.prototype.replaceAt = function(index, replacement) {
  return this.substring(0, index) + replacement + this.substring(index + replacement.length);
};

export default {
  mixins: [Resizable, DevelopmentModeDetector],
  components: {
    TermMasterResultDialog,
    ShareDialog,
    ErrorDialog,
    Keyboard,
    Hangman
  },
  computed: {
    english_term_length() {
      return count_symbols(this.game.key["EN"]);
    },
    greek_term_length() {
      return count_symbols(this.game.key["GR"]);
    },
    prompt() {
      if (this.game.max_timer > 0) {
        return "Attempts: " + this.game.failures + " of " + this.game.max_failures + " (" + this.game.timer + " of " + this.game.max_timer + " sec)";
      } else {
        return "Attempts: " + this.game.failures + " of " + this.game.max_failures + " (" + this.game.timer + " sec)";
      }
    },
    total_terms() {
      return (
        parseInt(this.statistics.teleterm_rows) +
        parseInt(this.statistics.inforterm_rows) +
        parseInt(this.statistics.termterm_rows) +
        parseInt(this.statistics.acouterm_rows) +
        parseInt(this.statistics.electerm_rows) +
        parseInt(this.statistics.lexiterm_rows)
      );
    },
    databaseOptions() {
      // Convert the databases object into an array for v-select
      const list = [
        {
          title: "Pick random",
          value: "random"
        },
        ...Object.keys(this.databases).map(key => {
          return {
            value: key,
            title: this.capitalizeFirstLetter(key) + " (" + this.databases[key]["en"] + ")",
            ...this.databases[key]
          };
        })
      ];

      return list;
    }
  },
  data() {
    return {
      statistics: {
        teleterm_rows: "?",
        inforterm_rows: "?",
        termterm_rows: "?",
        acouterm_rows: "?",
        electerm_rows: "?",
        lexiterm_rows: "?",
        emcterm_rows: "?",
        total_games: "0",
        games: 0,
        wins: 0
      },
      databases: [],
      game: {
        playing: false,
        failures: 0,
        max_failures: 10,
        secret: {
          GR: "",
          EN: ""
        },
        key: {
          GR: "",
          EN: ""
        },
        origin: {
          GR: "",
          EN: ""
        },
        metadata: "",
        term_uid: 0,
        timer: 0,
        max_timer: 0
      },
      input: {
        database: "random",
        allow_symbols: false,
        max_len_term_gr: 0,
        max_len_term_en: 0
      },
      parameters: {
        mobile: window.innerWidth <= 700
      }
    };
  },
  created() {
    this.statistics.games = parseInt(cookieService.getCookieOrCreate("total_games", 0));
    this.statistics.wins = parseInt(cookieService.getCookieOrCreate("wins", 0));

    wordMasterService
      .fetchStatistics()
      .then(response => {
        this.statistics.teleterm_rows = String(response.data.data[0].teleterm_rows);
        this.statistics.inforterm_rows = String(response.data.data[0].inforterm_rows);
        this.statistics.termterm_rows = String(response.data.data[0].termterm_rows);
        this.statistics.acouterm_rows = String(response.data.data[0].acouterm_rows);
        this.statistics.electerm_rows = String(response.data.data[0].electerm_rows);
        this.statistics.lexiterm_rows = String(response.data.data[0].lexiterm_rows);
        this.statistics.emcterm_rows = String(response.data.data[0].emcterm_rows);
        this.statistics.total_games = String(response.data.data[0].termmaster_games);
      })
      .catch();

    wordMasterService
      .fetchDatabases()
      .then(response => {
        // Filter to only include databases with termbase: true
        const allDatabases = response.data.data;
        this.databases = Object.keys(allDatabases)
          .filter(key => allDatabases[key].termbase === true)
          .reduce((filtered, key) => {
            filtered[key] = allDatabases[key];
            return filtered;
          }, {});
      })
      .catch(response => {
        console.log(response);
      });

    let id = this.$route.query.id;
    if (id) {
      this.startWithPrefinedTerm(id);
    }
  },
  methods: {
    // -----------------------------------------------------
    //                         Game
    // -----------------------------------------------------
    capitalizeFirstLetter(string) {
      return string.charAt(0).toUpperCase() + string.slice(1);
    },
    setDifficulty(max_len_term_gr, max_len_term_en) {
      this.input.max_len_term_gr = max_len_term_gr;
      this.input.max_len_term_en = max_len_term_en;
    },
    start() {
      wordMasterService
        .fetchRandomTerm(this.input.database, this.input.allow_symbols, this.input.max_len_term_gr, this.input.max_len_term_en)
        .then(this.initializeGame)
        .catch({});
    },
    async startWithPrefinedTerm(uid) {
      wordMasterService.fetchRandomTerm(this.input.database, this.input.allow_symbols, this.input.max_len_term_gr, this.input.max_len_term_en).then(
        wordMasterService
          .fetchTerm(uid)
          .then(this.initializeGame)
          .catch({})
      );
    },
    initializeGame(response) {
      if (response.data.data[0].length == 0) {
        this.$refs.errorDialog.open("Error", "No terms found. Please relax constraint limits. Use 0 for unlimited");
        return;
      }
      this.game.origin["EN"] = response.data.data[0].TERMEN;
      this.game.origin["GR"] = response.data.data[0].TERMGR;
      this.game.secret["EN"] = sanitize(response.data.data[0].TERMEN);
      this.game.secret["GR"] = sanitize(response.data.data[0].TERMGR);
      this.game.key["EN"] = mask(this.game.secret["EN"]);
      this.game.key["GR"] = mask(this.game.secret["GR"]);
      this.game.metadata = response.data.data[0];
      this.game.database = response.data.metadata["database"];
      this.game.term_uid = response.data.metadata["uid"];
      this.game.playing = true;
      this.game.timer = 0;
      this.game.failures = 0;
      this.statistics.games = parseInt(cookieService.getCookie("total_games")) + 1;
      this.statistics.total_games = response.data.metadata["termmaster_games"];
      cookieService.setCookie("total_games", this.statistics.games);
      this.$refs.keyboard_en.reset();
      this.$refs.keyboard_gr.reset();

      this.tick();
    },
    quit() {
      this.game.playing = false;
    },
    tick() {
      if (this.game.playing) {
        setTimeout(() => {
          this.game.timer++;

          if (this.game.max_timer > 0 && this.game.timer > this.game.max_timer) {
            this.gameOver(false);
            return;
          }
          this.tick();
        }, 1000);
      }
    },
    checkGameOver() {
      if (this.game.failures >= this.game.max_failures) {
        this.gameOver(false);
        return;
      }

      for (const languageKey of ["EN", "GR"]) {
        for (let i = 0; i < this.game.key[languageKey].length; i++) {
          if (this.game.key[languageKey][i] == "_") {
            return;
          }
        }
      }

      this.gameOver(true);
    },
    move(letter) {
      let hits = 0;

      for (const languageKey of ["EN", "GR"]) {
        for (let i = 0; i < this.game.key[languageKey].length; i++) {
          if (this.game.key[languageKey][i] == "_" && this.game.secret[languageKey][i] == letter) {
            this.game.key[languageKey] = this.game.key[languageKey].replaceAt(i, letter);
            hits++;
          }
        }
      }

      if (hits == 0) {
        this.game.failures++;
      }

      this.checkGameOver();
    },
    gameOver(win) {
      const uri = "https://nicomachus.codia.space/app/termbank/termmaster?id=" + this.game.term_uid;

      if (win) {
        cookieService.setCookie("wins", ++this.statistics.wins);
      }

      this.game.playing = false;

      this.$refs.resultDialog
        .open(win, this.game.origin["GR"], this.game.origin["EN"], this.game.database, this.game.metadata, uri, this.game.timer, this.total_terms)
        .then(() => {
          this.start();
        })
        .catch({});
    },
    share() {
      const uri = "https://nicomachus.codia.space/app/termbank/termmaster?id=" + this.game.term_uid;
      this.$refs.shareDialog.open(uri).catch({});
    }
  }
};
</script>

<style>
.flex-center {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.key_letter {
  background-color: rgba(255, 255, 255, 0.75);
  color: gray;
  line-height: 2.5rem;
  margin: 0 0.25rem 0.25rem;
}
</style>
